import { useEffect, useCallback, useState, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import {
	getFormFieldDataBackup,
	clearFormFieldDataBackup,
	markFormAsNavigating
} from '../../utils/formBackupUtils';
import { UseUnsavedChangesReturn } from './UnsavedChangesModal.types';

export const useUnsavedChanges = (formID: string | undefined): UseUnsavedChangesReturn => {
	const [showWarning, setShowWarning] = useState<boolean>(false);
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
	// Track both confirmed state and the formID it was confirmed for
	const navigationConfirmedRef = useRef<{ confirmed: boolean; forFormID: string | undefined }>({
		confirmed: false,
		forFormID: undefined
	});

	const hasUnsavedChanges = useCallback((): boolean => {
		if (!formID) return false;
		const backup = getFormFieldDataBackup(formID);
		return backup !== null && backup.fieldData && backup.fieldData.length > 0;
	}, [formID]);

	const blocker = useBlocker(hasUnsavedChanges);

	useEffect(() => {
		if (blocker.state === 'blocked') {
			setShowWarning(true);
		}
	}, [blocker.state]);

	const handleConfirmNavigation = useCallback((): void => {
		navigationConfirmedRef.current = { confirmed: true, forFormID: formID };
		if (formID) {
			markFormAsNavigating(formID);
			clearFormFieldDataBackup(formID);
		}
		setShowWarning(false);

		if (blocker.state === 'blocked' && blocker.proceed) {
			blocker.proceed();
		}

		if (pendingAction) {
			pendingAction();
			setPendingAction(null);
		}
	}, [formID, blocker, pendingAction]);

	const handleCancelNavigation = useCallback((): void => {
		setShowWarning(false);
		setPendingAction(null);

		if (blocker.state === 'blocked' && blocker.reset) {
			blocker.reset();
		}
	}, [blocker]);

	const checkAndWarnUnsavedChanges = useCallback(
		(onConfirmCallback?: () => void): boolean => {
			if (hasUnsavedChanges()) {
				setShowWarning(true);
				if (onConfirmCallback) {
					setPendingAction(() => onConfirmCallback);
				}
				return true;
			}
			return false;
		},
		[hasUnsavedChanges]
	);

	const onCancel = useCallback(
		(proceedWithCancel: () => void): void => {
			if (hasUnsavedChanges()) {
				setShowWarning(true);
				setPendingAction(() => proceedWithCancel);
				return;
			}
			proceedWithCancel();
		},
		[hasUnsavedChanges]
	);

	useEffect(() => {
		return () => {
			const { confirmed, forFormID } = navigationConfirmedRef.current;
			if (confirmed && forFormID === formID && formID) {
				clearFormFieldDataBackup(formID);
			}
		};
	}, [formID]);

	return {
		showWarning,
		handleConfirmNavigation,
		handleCancelNavigation,
		checkAndWarnUnsavedChanges,
		hasUnsavedChanges: hasUnsavedChanges(),
		onCancel
	};
};
